import { useCallback, useEffect, useMemo, useState } from "react";
import { testJSON } from "oolib";
import {
  SAMPLE_TEMPLATES,
  contentTemplates,
  ALL_TEMPLATES,
} from "../utils/sampleTemplates";
import { getTplLabel } from "../utils/getTplLabel";
import { buildDatasetGraph } from "../utils/buildDatasetGraph";
import { useGetQueryData } from "@/utils/react-query-hooks/general";

export const useTemplateSelection = (activeCase) => {
  const [customTemplates, setCustomTemplates] = useState({});
  const [selectedTplIds, setSelectedTplIds] = useState(activeCase?.selectedTplIds || []);
  const [customTemplateInput, setCustomTemplateInput] = useState("");
  const [customTemplateFeedback, setCustomTemplateFeedback] = useState(null);
  const [usePlatformTemplates, setUsePlatformTemplates] = useState(true);
  
  // Get all platform templates
  const platformAllTpls = useGetQueryData("allTpls") || [];

  // Convert platform templates array to object keyed by kp_content_type
  // Filter to only include publishing and collections templates, exclude userProfiles
  const platformTemplatesObj = useMemo(() => {
    const obj = {};
    const allowedSegments = ['publishing'];
    platformAllTpls.forEach(tpl => {
      if (
        tpl?.kp_content_type &&
        allowedSegments.includes(tpl?.general?.segment) &&
        tpl?.category !== 'userProfiles'
      ) {
        obj[tpl.kp_content_type] = tpl;
      }
    });
    return obj;
  }, [platformAllTpls]);

  useEffect(() => {
    if (activeCase?.selectedTplIds) {
      setSelectedTplIds(activeCase.selectedTplIds);
    } else if (usePlatformTemplates && Object.keys(platformTemplatesObj).length > 0) {
      // Default: select all platform templates
      setSelectedTplIds(Object.keys(platformTemplatesObj));
    }
  }, [activeCase, platformTemplatesObj]);

  // Determine base templates based on switch
  const baseTemplates = useMemo(
    () => usePlatformTemplates ? platformTemplatesObj : SAMPLE_TEMPLATES,
    [usePlatformTemplates, platformTemplatesObj]
  );

  const baseContentTemplates = useMemo(() => {
    if (usePlatformTemplates) {
      // platformTemplatesObj is already filtered for publishing/collections and non-userProfiles
      return platformTemplatesObj;
    }
    return contentTemplates;
  }, [usePlatformTemplates, platformTemplatesObj]);

  const baseAllTemplates = useMemo(() => {
    if (usePlatformTemplates) {
      // Filter platform templates to only include publishing and collections templates
      const allowedSegments = ['publishing', 
        // 'collections'
      ];
      return platformAllTpls.filter(tpl => 
        allowedSegments.includes(tpl?.general?.segment) &&
        tpl?.category !== 'userProfiles'
      );
    }
    return ALL_TEMPLATES;
  }, [usePlatformTemplates, platformAllTpls]);

  const runtimeTemplates = useMemo(
    () => ({ ...baseTemplates, ...customTemplates }),
    [baseTemplates, customTemplates]
  );

  const runtimeContentTemplates = useMemo(() => {
    const base = { ...baseContentTemplates };
    Object.entries(customTemplates).forEach(([id, tpl]) => {
      if (tpl?.category !== "userProfiles") {
        base[id] = tpl;
      }
    });
    return base;
  }, [baseContentTemplates, customTemplates]);

  const allTemplates = useMemo(
    () => [...baseAllTemplates, ...Object.values(customTemplates)],
    [baseAllTemplates, customTemplates]
  );

  const existingContentTypes = useMemo(
    () => new Set(allTemplates.map((tpl) => tpl.kp_content_type)),
    [allTemplates]
  );

  // Classify each template as a dataset ("content") vs a tag collection ("tag")
  // using the same logic as the connections graph, so ordering stays consistent.
  const tagContentTypes = useMemo(() => {
    const { nodes } = buildDatasetGraph(Object.values(runtimeContentTemplates));
    return new Set(nodes.filter((n) => n.kind === "tag").map((n) => n.id));
  }, [runtimeContentTemplates]);

  const templateOptions = useMemo(() => {
    const opts = Object.entries(runtimeContentTemplates).map(([id, tpl]) => ({
      value: id,
      display: getTplLabel(tpl),
      desc: tpl.kp_content_type,
    }));
    // Datasets (content) on top, tag collections at the bottom; stable within each group.
    return opts
      .map((o, i) => ({ o, i, isTag: tagContentTypes.has(o.value) }))
      .sort((a, b) => Number(a.isTag) - Number(b.isTag) || a.i - b.i)
      .map((d) => d.o);
  }, [runtimeContentTemplates, tagContentTypes]);

  const templateLookup = useMemo(() => {
    const map = {};
    templateOptions.forEach((option) => {
      map[option.value] = option;
    });
    return map;
  }, [templateOptions]);

  const selectedTemplateOptions = useMemo(
    () =>
      selectedTplIds
        .map((value) => templateLookup[value])
        .filter((option) => Boolean(option)),
    [selectedTplIds, templateLookup]
  );

  const selectedTpls = useMemo(
    () =>
      selectedTplIds
        .map((tplId) => runtimeTemplates[tplId])
        .filter((tpl) => Boolean(tpl)),
    [selectedTplIds, runtimeTemplates]
  );

  const generatedContentTypes = useMemo(
    () =>
      Array.from(
        new Set(
          selectedTpls
            .map((tpl) => tpl?.kp_content_type)
            .filter((contentType) => Boolean(contentType))
        )
      ),
    [selectedTpls]
  );

  const handleTemplateToggle = useCallback((isSelected, option) => {
    setSelectedTplIds((prev) => {
      if (isSelected) {
        return prev.filter((value) => value !== option.value);
      }
      return [...prev, option.value];
    });
  }, []);

  const handleToggleTemplateSource = useCallback((useplatform) => {
    setUsePlatformTemplates(useplatform);
    if (useplatform) {
      // Select all platform templates by default
      const allPlatformIds = Object.keys(platformTemplatesObj);
      setSelectedTplIds(allPlatformIds);
    } else {
      setSelectedTplIds([]);
    }
  }, [platformTemplatesObj]);

  const handleCustomTemplateInputChange = useCallback((value) => {
    setCustomTemplateInput(value);
    setCustomTemplateFeedback(null);
  }, []);

  const handleAddCustomTemplate = useCallback(() => {
    setCustomTemplateFeedback(null);
    const raw = customTemplateInput.trim();

    if (!raw) {
      setCustomTemplateFeedback({
        type: "error",
        message: "Paste a template JSON to add.",
      });
      return;
    }

    if (!testJSON(raw)) {
      setCustomTemplateFeedback({
        type: "error",
        message: "Provided input is not valid JSON.",
      });
      return;
    }

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      setCustomTemplateFeedback({
        type: "error",
        message: "Unable to parse template JSON.",
      });
      return;
    }

    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      setCustomTemplateFeedback({
        type: "error",
        message: "Template should be a JSON object.",
      });
      return;
    }

    if (typeof parsed.kp_content_type !== "string" || !parsed.kp_content_type.trim()) {
      setCustomTemplateFeedback({
        type: "error",
        message: "Template must include a valid kp_content_type string.",
      });
      return;
    }

    if (!parsed.kp_templates || typeof parsed.kp_templates !== "object") {
      setCustomTemplateFeedback({
        type: "error",
        message: "Template must include kp_templates with at least one builder section.",
      });
      return;
    }

    if (typeof parsed.category !== "string" || !parsed.category.trim()) {
      setCustomTemplateFeedback({
        type: "error",
        message: "Template must include a category value.",
      });
      return;
    }

    if (existingContentTypes.has(parsed.kp_content_type)) {
      setCustomTemplateFeedback({
        type: "error",
        message: `A template with kp_content_type "${parsed.kp_content_type}" already exists.`,
      });
      return;
    }

    const hasBuilderConfigs = Object.values(parsed.kp_templates || {}).some((section) => {
      if (!section) return false;
      if (Array.isArray(section)) return section.length > 0;
      if (typeof section === "object") {
        if (Array.isArray(section.configs) && section.configs.length > 0) return true;
        if (section.builder && section.configs) return true;
        if (
          section.inputs &&
          typeof section.inputs === "object" &&
          Object.keys(section.inputs).length > 0
        )
          return true;
        if (
          section.configs &&
          typeof section.configs === "object" &&
          Object.keys(section.configs).length > 0
        )
          return true;
      }
      return false;
    });

    if (!hasBuilderConfigs) {
      setCustomTemplateFeedback({
        type: "error",
        message: "kp_templates should contain at least one configured block.",
      });
      return;
    }

    const templateId = parsed.kp_content_type;

    setCustomTemplates((prev) => ({
      ...prev,
      [templateId]: parsed,
    }));
    setCustomTemplateInput("");
    setCustomTemplateFeedback({
      type: "success",
      message: `Template "${templateId}" added.`,
    });
    setSelectedTplIds((prev) => (prev.includes(templateId) ? prev : [...prev, templateId]));
  }, [customTemplateInput, existingContentTypes]);

  return {
    selectedTpls,
    templateOptions,
    selectedTemplateOptions,
    handleTemplateToggle,
    customTemplateInput,
    handleCustomTemplateInputChange,
    handleAddCustomTemplate,
    customTemplateFeedback,
    generatedContentTypes,
    allTemplates,
    usePlatformTemplates,
    handleToggleTemplateSource,
  };
};


